import type { NoddiAsyncError } from 'noddi-types';
import { ApiErrorMessageInternal, NoddiInfoBanner, useAppForm } from 'noddi-ui-web';
import { addMemberErrorMessage } from './addMemberErrorMessage';
import { AddMemberStepLayout } from './AddMemberStepLayout';
import { addMemberSettingsSchema, type AddMemberSettingsValues } from './addMemberWizardSteps';
import { MemberContactInfo } from './MemberContactInfo';
import type { NotificationGroupOverviewTranslations } from './notificationTranslations';

const TOGGLE_ROW_CLASS = 'rounded-xl border border-border/60 p-3';
const FORM_ID = 'add-member-settings-form';

type Props = {
  /** Who the wizard is about to add, so the last decision is made with the person in view. */
  name: string;
  phoneNumber: string;
  email?: string;
  /** Held by the dialog, so stepping back and forward returns to the toggles as they were left. */
  values: AddMemberSettingsValues;
  error: NoddiAsyncError | null;
  translations: NotificationGroupOverviewTranslations;
  onBack: (values: AddMemberSettingsValues) => void;
  onSubmit: (values: AddMemberSettingsValues) => Promise<void>;
};

export const AddMemberSettingsStep = ({
  name,
  phoneNumber,
  email,
  values,
  error,
  translations: t,
  onBack,
  onSubmit
}: Props) => {
  const translatedError = addMemberErrorMessage(error, t);
  const form = useAppForm({
    defaultValues: values,
    validators: { onSubmit: addMemberSettingsSchema },
    onSubmit: ({ value }) => onSubmit(value)
  });

  return (
    <form.AppForm>
      <form
        id={FORM_ID}
        className='flex min-h-0 flex-1 flex-col gap-4'
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          form.handleSubmit();
        }}
      >
        <AddMemberStepLayout
          secondary={{ label: t.back, onClick: () => onBack(form.state.values), icon: 'ArrowLeft' }}
          action={<form.SubscribeButton formId={FORM_ID} label={t.addMember} className='flex-[2]' />}
        >
          <div className={TOGGLE_ROW_CLASS}>
            <MemberContactInfo firstName={name} lastName='' email={email} phoneNumber={phoneNumber} />
          </div>
          <section className='flex flex-col gap-2'>
            <div className={TOGGLE_ROW_CLASS}>
              <form.AppField name='isAdmin'>
                {(field) => (
                  <field.Switch label={t.groupAdmin} description={t.groupAdminDescription} switchPosition='end' />
                )}
              </form.AppField>
            </div>
            <div className={TOGGLE_ROW_CLASS}>
              <form.AppField name='sendSms'>
                {(field) => (
                  <field.Switch label={t.sendAddedSms} description={t.sendAddedSmsDescription} switchPosition='end' />
                )}
              </form.AppField>
            </div>
          </section>
          {error &&
            (translatedError ? (
              <div className='mt-2'>
                <NoddiInfoBanner variant='error' texts={[{ value: translatedError }]} />
              </div>
            ) : (
              <ApiErrorMessageInternal error={error} />
            ))}
        </AddMemberStepLayout>
      </form>
    </form.AppForm>
  );
};
